<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<openapi.AddressBookGnafSuburbLookupListContentOpenapi>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'locality_name',
      title: 'Suburb',
    },
    {
      value: 'postcode',
      title: 'Postcode',
    },
    {
      value: 'state',
      title: 'State',
    },
    {
      value: 'locality_alias_name',
      title: 'Suburb alias',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.stickyHeader,
      $style.suburbsSearchTable,
    ]"
    data-testid="suburbsSearchTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.unique_key">
        <tr>
          <td :class="$style.locality_nameTd">
            <EmDashPlaceholder :item="dataItem.locality_name" />
          </td>
          <td :class="$style.postcodeTd">
            <EmDashPlaceholder :item="dataItem.postcode" />
          </td>
          <td :class="$style.stateTd">
            <EmDashPlaceholder :item="dataItem.state" />
          </td>
          <td :class="$style.locality_alias_nameTd">
            <EmDashPlaceholder :item="dataItem.locality_alias_name" />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';
@use '@/styles/table.module.scss' as tableStyles;

.suburbsSearchTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}

  .locality_nameTh {
    @include tableStyles.column-flexible;
  }
  .postcodeTh {
    @include tableStyles.column-regular;
  }
  .stateTh {
    @include tableStyles.column-medium;
  }
  .locality_alias_nameTh {
    @include tableStyles.column-flexible;
  }
</style>
